import { useCallback, useState } from "react";
import { useAppPartySocket } from "./useAppPartySocket";

// Map our connection status to the existing type
export type PartyServerConnectionStatus =
  | "connected"
  | "disconnected"
  | "reconnecting"
  | "server_restarting";

interface UsePartySocketWithReconnectOptions {
  room: string;
  party: string;
  onMessage: (event: MessageEvent) => void;
}

/**
 * Simplified reconnection hook that uses the stable WebSocket implementation.
 * The complex thrashing detection and manual reconnection logic is now handled
 * by react-use-websocket internally.
 */
export function usePartySocketWithReconnect({
  room,
  party,
  onMessage,
}: UsePartySocketWithReconnectOptions) {
  // Track connection attempts for UI feedback
  const [connectionAttempts, setConnectionAttempts] = useState(0);

  const socket = useAppPartySocket({
    room,
    party,
    onOpen: () => {
      console.log(`✅ Connected to party: ${party}, room: ${room}`);
      setConnectionAttempts(0); // Reset on successful connection
    },
    onClose: (event: CloseEvent) => {
      console.log(
        `🔌 Disconnected from party: ${party}, room: ${room} (code: ${event.code})`,
      );
      setConnectionAttempts((prev) => prev + 1);
    },
    onError: (error: Event) => {
      console.error(
        `❌ WebSocket error for party: ${party}, room: ${room}`,
        error,
      );
    },
    onMessage,
    onReconnectStop: (numAttempts: number) => {
      console.warn(`🛑 Stopped reconnecting after ${numAttempts} attempts`);
      setConnectionAttempts(numAttempts);
    },
  });

  // Map our enhanced connection status to the existing interface
  const partyServerConnectionStatus: PartyServerConnectionStatus = (() => {
    switch (socket.connectionStatus) {
      case "connected":
        return "connected";
      case "connecting":
        return "reconnecting";
      case "server_restarting":
        return "server_restarting";
      default:
        return "disconnected";
    }
  })();

  // Force reconnect method (rarely needed with the new implementation)
  const forceReconnect = useCallback(() => {
    console.log("🔄 Force reconnecting...");
    const ws = socket.getWebSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close(1000, "Manual reconnect");
    }
  }, [socket]);

  return {
    socket,
    partyServerConnectionStatus,
    connectionAttempts,
    isDeploymentReconnecting: socket.isDeploymentReconnecting,
    forceReconnect,
    // Backward compatibility
    reconnectAttempt: connectionAttempts,
    isThrashing: false, // No longer needed - handled internally
    shouldSuggestRefresh: connectionAttempts > 10, // Suggest refresh after many attempts
  };
}
